import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { cookie } from '../../utils/cookie';
import gtmService from '../../utils/gtm';
import {
  updateConsentMode,
  grantAllConsent as grantAllConsentMode,
  denyAllConsent as denyAllConsentMode
} from '../../utils/consentMode';
import {
  loadConsentFromStorage,
  acceptAllConsent,
  rejectAllConsent,
  updateConsent,
  updateConsentSettings,
  resetConsent,
  selectShowBanner,
  selectConsentSettings,
  selectIsConsentLoaded
} from '../../store/slices/consentSlice';
import CookieBanner from './CookieBanner';
import CookiePreferencesModal from './CookiePreferencesModal';
import {
  COOKIE_CATEGORIES,
  LEGACY_COOKIE_NAME,
  COOKIE_EXPIRY_DAYS
} from './constants';
import './styles.css';

const CookieConsentBanner = () => {
  const dispatch = useDispatch();
  const showBanner = useSelector(selectShowBanner);
  const consentSettings = useSelector(selectConsentSettings);
  const isConsentLoaded = useSelector(selectIsConsentLoaded);

  const [showPreferences, setShowPreferences] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initializeConsent = async () => {
      try {
        dispatch(loadConsentFromStorage());

        // Check for legacy cookie using our custom cookie utility
        const legacyCookie = cookie.get(LEGACY_COOKIE_NAME);
        if (legacyCookie === 'accepted') {
          dispatch(acceptAllConsent());
          // Sync with Google Consent Mode
          grantAllConsentMode();
        } else if (legacyCookie === 'rejected') {
          dispatch(rejectAllConsent());
          // Sync with Google Consent Mode
          denyAllConsentMode();
        }
      } catch (error) {
        setError('Failed to load cookie preferences');
      }
    };

    initializeConsent();
  }, [dispatch]);

  // Note: GTM is now initialized immediately on page load (src/index.js)
  // for cookieless tracking. No need to initialize it here.

  const setLegacyCookie = useCallback((value) => {
    try {
      // Use setWithDomain for cross-subdomain cookie support
      // Convert days to seconds for our cookie utility
      const expirySeconds = COOKIE_EXPIRY_DAYS * 24 * 3600;
      cookie.setWithDomain(LEGACY_COOKIE_NAME, value, { expire: expirySeconds });
      console.log(`✅ Legacy cookie "${LEGACY_COOKIE_NAME}" set to "${value}" for all subdomains`);
    } catch (error) {
      console.error('Failed to set legacy cookie:', error);
    }
  }, []);

  const trackConsentUpdate = useCallback(async (settings) => {
    try {
      await gtmService.trackConsentUpdate(settings);
    } catch (error) {
      console.error('Failed to track consent update:', error);
    }
  }, []);

  const handleAcceptAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      dispatch(acceptAllConsent());
      setLegacyCookie('accepted');

      // Update Google Consent Mode (GTM already loaded from index.js)
      grantAllConsentMode();

      // Track consent update
      await trackConsentUpdate({
        analytics: true,
        marketing: true,
        preferences: true
      });
    } catch (error) {
      setError('Failed to save preferences');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, setLegacyCookie, trackConsentUpdate]);

  const handleRejectAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      dispatch(rejectAllConsent());
      setLegacyCookie('rejected');

      // IMPORTANT: Update Google Consent Mode to denied state
      denyAllConsentMode();

      await trackConsentUpdate({
        analytics: false,
        marketing: false,
        preferences: false
      });
    } catch (error) {
      setError('Failed to save preferences');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, setLegacyCookie, trackConsentUpdate]);

  const handleCustomize = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const handleClosePreferences = useCallback(() => {
    setShowPreferences(false);
    setError(null);
  }, []);

  const handleSavePreferences = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const timestamp = new Date().toISOString();

      dispatch(
        updateConsent({
          consentSettings,
          timestamp
        })
      );

      const hasAnyConsent = Object.entries(consentSettings)
        .filter(([key]) => key !== 'necessary')
        .some(([, value]) => value);

      setLegacyCookie(hasAnyConsent ? 'accepted' : 'rejected');

      // Update Google Consent Mode with granular settings (GTM already loaded)
      updateConsentMode({
        analytics: consentSettings.analytics,
        marketing: consentSettings.marketing,
        preferences: consentSettings.preferences
      });

      // Track consent update
      await trackConsentUpdate(consentSettings);

      setShowPreferences(false);
    } catch (error) {
      setError('Failed to save preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, consentSettings, setLegacyCookie, trackConsentUpdate]);

  const handleToggleConsent = useCallback(
    (type) => {
      if (type === 'necessary') return; // Can't disable necessary cookies

      dispatch(
        updateConsentSettings({
          type,
          value: !consentSettings[type]
        })
      );
    },
    [dispatch, consentSettings]
  );

  const handleResetConsent = useCallback(() => {
    try {
      dispatch(resetConsent());
      // Use eraseAll to remove legacy cookie from all domains
      cookie.eraseAll(LEGACY_COOKIE_NAME);
      console.log(`🗑️  Legacy cookie "${LEGACY_COOKIE_NAME}" removed from all subdomains`);
      setError(null);
    } catch (error) {
      setError('Failed to reset preferences');
    }
  }, [dispatch]);

  // Expose reset function for Footer component (better implementation)
  useEffect(() => {
    window.resetCookieConsent = handleResetConsent;

    return () => {
      delete window.resetCookieConsent;
    };
  }, [handleResetConsent]);

  // Memoized cookie categories for performance
  const cookieCategories = useMemo(() => COOKIE_CATEGORIES, []);

  if (!showBanner) return null;

  return (
    <>
      <CookieBanner
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectAll}
        onCustomize={handleCustomize}
        isLoading={isLoading}
        error={error}
      />

      {showPreferences && (
        <CookiePreferencesModal
          categories={cookieCategories}
          consentSettings={consentSettings}
          onToggleConsent={handleToggleConsent}
          onSave={handleSavePreferences}
          onClose={handleClosePreferences}
          isLoading={isLoading}
          error={error}
        />
      )}
    </>
  );
};

export default CookieConsentBanner;
